import React from 'react'
import clsx from 'clsx'
import { Loader2 } from 'lucide-react'
import { designTokens } from '../../lib/design-tokens'

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'gaming'
type ButtonSize = 'sm' | 'md' | 'lg'
type ButtonState = 'default' | 'hover' | 'active' | 'disabled' | 'loading'

const getButtonClasses = (variant: ButtonVariant, size: ButtonSize, loading: boolean, glow: boolean = false) => {
  const baseClasses = 'btn focus-ring inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 disabled:pointer-events-none disabled:opacity-50'
  
  const variantClasses = {
    primary: 'btn-primary shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
    secondary: 'btn-secondary shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
    outline: 'btn-outline hover:shadow-md hover:scale-[1.02] active:scale-[0.98]',
    ghost: 'btn-ghost hover:scale-[1.02] active:scale-[0.98]',
    danger: 'btn-danger shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]',
    success: 'bg-green-500 text-white border border-green-500 hover:bg-green-600 hover:border-green-600 hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg',
    gaming: 'bg-gradient-to-r from-orange-500 to-red-500 text-slate-950 border border-orange-500 hover:from-orange-400 hover:to-red-500 hover:scale-105 active:scale-95 shadow-glow hover:shadow-glow-lg transition-all duration-300'
  }
  
  const sizeClasses = {
    sm: 'btn-sm h-9 px-3 text-sm',
    md: 'btn-md h-11 px-4 text-sm',
    lg: 'btn-lg h-12 px-6 text-base',
  }
  
  const glowClasses = glow ? 'animate-pulse shadow-glow' : ''
  
  return clsx(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    glowClasses,
    loading && 'cursor-not-allowed'
  )
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  glow?: boolean
  badge?: string
  state?: 'selected' | 'eliminated' | 'correct' | 'incorrect'
  celebration?: () => void
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant = 'primary',
    size = 'md',
    loading = false, 
    leftIcon, 
    rightIcon, 
    children, 
    disabled,
    glow = false,
    badge,
    state,
    celebration,
    onClick,
    ...props 
  }, ref) => {
    const isDisabled = disabled || loading

    // Handle click with celebration
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (celebration) {
        celebration()
      }
      if (onClick) {
        onClick(e)
      }
    }

    // Apply state-based styling
    const stateClasses = state ? {
      selected: 'border-orange-500 bg-orange-500/10 text-orange-400',
      eliminated: 'border-gray-600 bg-gray-700 text-gray-500 opacity-50 cursor-not-allowed',
      correct: 'border-green-500 bg-green-500/10 text-green-400',
      incorrect: 'border-red-500 bg-red-500/10 text-red-400'
    }[state] : ''

    return (
      <button
        className={clsx(
          getButtonClasses(variant, size, loading, glow), 
          stateClasses,
          className
        )}
        ref={ref}
        disabled={isDisabled || state === 'eliminated'}
        onClick={handleClick}
        {...props}
      >
        <div className="relative flex items-center justify-center">
          {loading && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          {!loading && leftIcon && (
            <span className="mr-2">{leftIcon}</span>
          )}
          {children}
          {!loading && rightIcon && (
            <span className="ml-2">{rightIcon}</span>
          )}
          
          {/* Badge overlay */}
          {badge && (
            <span className="absolute -top-2 -right-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
              {badge}
            </span>
          )}
        </div>
      </button>
    )
  }
)

Button.displayName = 'Button'

export { Button }
